import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Megaphone } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { StatusBadge } from '@/components/common/Badge';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { SelectField, TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useCampusPicker } from '@/hooks/usePickers';
import { announcementsApi } from '@/services/api/endpoints/notifications';
import { sectionsApi } from '@/services/api/endpoints/academic';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type {
  Announcement,
  AnnouncementAudience,
  AnnouncementInput,
  AnnouncementStatus,
} from '@/types/notification';
import { formatDateTime, humanise } from '@/utils/format';

const STATUSES: AnnouncementStatus[] = ['DRAFT', 'PUBLISHED', 'ARCHIVED'];

const AUDIENCES: AnnouncementAudience[] = ['ALL', 'STAFF', 'TEACHERS', 'STUDENTS', 'PARENTS'];

/** The audiences a section may narrow. The server refuses a section on the rest. */
const SECTION_AUDIENCES: AnnouncementAudience[] = ['ALL', 'STUDENTS', 'PARENTS'];

const TEXTAREA =
  'block w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink shadow-xs ' +
  'placeholder:text-subtle transition-[border-color,box-shadow] duration-150 ' +
  'hover:border-line-strong ' +
  'focus:border-accent focus:outline-none focus:ring-2 focus:ring-[var(--sms-accent-ring)]';

/** An instant as a `datetime-local` value, in the viewer's own timezone. */
function toLocalInput(value: string | undefined): string {
  if (value === undefined || value === '') {
    return '';
  }
  const date = new Date(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

type PendingAction = { kind: 'publish' | 'archive'; announcement: Announcement };

/**
 * The announcement desk.
 *
 * <p>Every announcement the school has written, drafts included, one page at a
 * time. A draft is edited freely; publishing puts it in front of its audience;
 * archiving takes it down and keeps it as history. Only drafts are editable —
 * the server refuses anything else, and its message is shown as it comes.
 *
 * <p>Who ends up seeing a notice is resolved on the server from the audience,
 * campus and section chosen here. Nothing on this page previews or narrows a
 * reader's feed.
 */
export function AnnouncementsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [status, setStatusFilter] = useState<AnnouncementStatus | ''>('');

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Announcement | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState<AnnouncementAudience>('ALL');
  const [campusId, setCampusId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const [pending, setPending] = useState<PendingAction | null>(null);

  const formOpen = adding || editing !== null;
  const sectionAllowed = SECTION_AUDIENCES.includes(audience);

  const campuses = useCampusPicker();

  const sectionParams = {
    page: 0,
    size: 200,
    ...(campusId === '' ? {} : { campusId }),
  };
  const sections = useQuery({
    queryKey: queryKeys.sections.list(sectionParams),
    queryFn: () => sectionsApi.list(sectionParams),
    enabled: formOpen && sectionAllowed && can(PERMISSIONS.SECTION_VIEW),
    staleTime: 5 * 60_000,
  });

  const params = {
    page,
    size,
    ...(status === '' ? {} : { status }),
  };

  const announcements = useQuery({
    queryKey: queryKeys.announcements.list(params),
    queryFn: () => announcementsApi.list(params),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.announcements.all });
  };

  function open(announcement: Announcement | null) {
    setEditing(announcement);
    setAdding(announcement === null);
    setTitle(announcement?.title ?? '');
    setBody(announcement?.body ?? '');
    setAudience(announcement?.audience ?? 'ALL');
    setCampusId(announcement?.campusId ?? '');
    setSectionId(announcement?.sectionId ?? '');
    setExpiresAt(toLocalInput(announcement?.expiresAt));
  }

  function closeForm() {
    setAdding(false);
    setEditing(null);
  }

  const save = useMutation({
    mutationFn: () => {
      const payload: AnnouncementInput = {
        title: title.trim(),
        body: body.trim(),
        audience,
        campusId: campusId === '' ? undefined : campusId,
        sectionId: !sectionAllowed || sectionId === '' ? undefined : sectionId,
        expiresAt: expiresAt === '' ? undefined : new Date(expiresAt).toISOString(),
      };
      return editing === null
        ? announcementsApi.create(payload)
        : announcementsApi.update(editing.id, payload);
    },
    onSuccess: (announcement) => {
      toast.success(`"${announcement.title}" saved as a draft`);
      closeForm();
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const act = useMutation({
    mutationFn: (action: PendingAction) =>
      action.kind === 'publish'
        ? announcementsApi.publish(action.announcement.id)
        : announcementsApi.archive(action.announcement.id),
    onSuccess: (announcement) => {
      toast.success(`"${announcement.title}" is now ${humanise(announcement.status).toLowerCase()}`);
      setPending(null);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const columns: Column<Announcement>[] = [
    {
      key: 'title',
      header: 'Announcement',
      render: (row) => (
        <span className="block min-w-[180px]">
          <span className="block text-sm font-semibold text-ink">{row.title}</span>
          <span className="line-clamp-1 block text-[11px] text-subtle">{row.body}</span>
        </span>
      ),
    },
    {
      key: 'audience',
      header: 'Audience',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{humanise(row.audience)}</span>
          <span className="block text-[11px] text-subtle">
            {[row.campusName ?? 'Whole school', row.sectionName].filter(Boolean).join(' · ')}
          </span>
        </span>
      ),
    },
    {
      key: 'published',
      header: 'Published',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{formatDateTime(row.publishedAt)}</span>
          <span className="block text-[11px] text-subtle">
            {row.expiresAt === undefined ? 'Until archived' : `Until ${formatDateTime(row.expiresAt)}`}
          </span>
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) => (
        <span className="flex justify-end gap-1.5">
          {row.status === 'DRAFT' && (
            <>
              <Button size="sm" variant="secondary" onClick={() => open(row)}>
                Edit
              </Button>
              <Button size="sm" onClick={() => setPending({ kind: 'publish', announcement: row })}>
                Publish
              </Button>
            </>
          )}
          {row.status !== 'ARCHIVED' && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setPending({ kind: 'archive', announcement: row })}
            >
              Archive
            </Button>
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <ListPage
        title="Announcement Desk"
        description="Draft, publish and archive notices. Drafts are private to the desk until published."
        icon={<Megaphone className="h-5 w-5" aria-hidden="true" />}
        action={<Button onClick={() => open(null)}>New announcement</Button>}
        filters={
          <FilterSelect
            label="Status"
            value={status}
            onChange={(value) => {
              setStatusFilter(value);
              setPage(0);
            }}
            options={STATUSES.map((value) => ({ value, label: humanise(value) }))}
          />
        }
        pagination={announcements.data?.pagination}
        onPageChange={setPage}
        onSizeChange={(next) => {
          setSize(next);
          setPage(0);
        }}
      >
        <DataTable
          columns={columns}
          rows={announcements.data?.data}
          rowKey={(row) => row.id}
          loading={announcements.isPending}
          error={announcements.error}
          onRetry={() => void announcements.refetch()}
          emptyTitle={status === '' ? 'No announcements yet' : 'Nothing with this status'}
          emptyDescription={
            status === ''
              ? 'Write the first notice; it stays a draft until you publish it.'
              : 'Clear the status filter to see every announcement.'
          }
        />
      </ListPage>

      <Modal
        open={formOpen}
        onClose={closeForm}
        title={editing === null ? 'New announcement' : `Edit "${editing.title}"`}
        description="Saved as a draft. Nobody outside the desk sees it until it is published."
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={closeForm}>
              Cancel
            </Button>
            <Button
              onClick={() => save.mutate()}
              loading={save.isPending}
              disabled={title.trim() === '' || body.trim() === ''}
            >
              Save draft
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <TextField
            label="Title"
            value={title}
            maxLength={200}
            onChange={(event) => setTitle(event.target.value)}
            required
          />
          <div>
            <label htmlFor="announcement-body" className="mb-1.5 block text-xs font-medium text-muted">
              Message
              <span className="ml-0.5 text-danger">*</span>
            </label>
            <textarea
              id="announcement-body"
              value={body}
              rows={6}
              maxLength={4000}
              onChange={(event) => setBody(event.target.value)}
              className={TEXTAREA}
              required
            />
            <p className="mt-1.5 text-xs text-subtle">{body.length} / 4000</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <SelectField
              label="Audience"
              value={audience}
              onChange={(event) => {
                const next = event.target.value as AnnouncementAudience;
                setAudience(next);
                if (!SECTION_AUDIENCES.includes(next)) {
                  setSectionId('');
                }
              }}
              required
            >
              {AUDIENCES.map((value) => (
                <option key={value} value={value}>
                  {value === 'ALL' ? 'Everyone' : humanise(value)}
                </option>
              ))}
            </SelectField>
            <TextField
              label="Expires"
              type="datetime-local"
              value={expiresAt}
              onChange={(event) => setExpiresAt(event.target.value)}
              hint="Leave blank to show it until archived."
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <SelectField
              label="Campus"
              value={campusId}
              onChange={(event) => {
                setCampusId(event.target.value);
                setSectionId('');
              }}
            >
              <option value="">Whole school</option>
              {(campuses.data ?? []).map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </SelectField>
            {sectionAllowed && (
              <SelectField
                label="Section"
                value={sectionId}
                onChange={(event) => setSectionId(event.target.value)}
                disabled={sections.isFetching && sections.data === undefined}
                hint="Narrows students and parents to one class."
              >
                <option value="">Every section</option>
                {(sections.data?.data ?? []).map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.gradeName} · {row.name}
                    {campusId === '' && row.campusName !== undefined ? ` (${row.campusName})` : ''}
                  </option>
                ))}
              </SelectField>
            )}
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={pending !== null}
        title={
          pending === null
            ? ''
            : pending.kind === 'publish'
              ? `Publish "${pending.announcement.title}"?`
              : `Archive "${pending.announcement.title}"?`
        }
        message={
          pending?.kind === 'publish'
            ? 'It goes live for its audience straight away and can no longer be edited.'
            : 'It stops showing in every feed. The record is kept, but it cannot be republished.'
        }
        confirmLabel={pending?.kind === 'publish' ? 'Publish' : 'Archive'}
        destructive={pending?.kind === 'archive'}
        loading={act.isPending}
        onConfirm={() => {
          if (pending !== null) {
            act.mutate(pending);
          }
        }}
        onCancel={() => setPending(null)}
      />
    </>
  );
}
